import { useState, useRef, useEffect } from "react";
import { MessageSquare, Trash2, Loader2 } from "lucide-react";
import { useConversations, useRenameConversation, useDeleteConversation } from "@/hooks/useConversations";
import { useModels } from "@/hooks/useModels";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigation } from "@/contexts/NavigationContext";
import { useGeneration } from "@/contexts/GenerationRegistry";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { NavTab } from "@/types/navigation";

interface ConversationListProps {
  projectId?: string;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  navigateToTab: (tab: NavTab) => void;
  useGreenHighlight: boolean;
  fetchAll?: boolean;
  onBeforeSelect?: (conv: any) => void;
}

export const ConversationList = ({
  projectId,
  activeConversationId,
  setActiveConversationId,
  navigateToTab,
  useGreenHighlight,
  fetchAll,
  onBeforeSelect,
}: ConversationListProps) => {
  const { data: conversations } = useConversations(projectId, fetchAll);
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();
  const isMobile = useIsMobile();
  const { setSidebarCollapsed } = useNavigation();
  const generation = useGeneration();

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const handleRenameSubmit = () => {
    if (renamingId && renameValue.trim()) {
      renameConversation.mutate({ id: renamingId, title: renameValue.trim() });
    }
    setRenamingId(null);
  };

  const handleDelete = () => {
    if (!pendingDeleteId) return;
    deleteConversation.mutate(pendingDeleteId, {
      onSuccess: () => {
        if (activeConversationId === pendingDeleteId) {
          setActiveConversationId(null);
        }
      },
    });
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
  };

  return (
    <>
      <div className="pl-4 space-y-0.5 py-1">
        {conversations?.map((conv) => {
          const isActive = activeConversationId === conv.id;
          const isRenaming = renamingId === conv.id;
          // Gerando: sessão viva local OU flag do servidor (recuperação pós-refresh).
          const isGenerating =
            generation.isGenerating(conv.id) || (conv as any).generation_status === "generating";
          // Resposta nova/falha: só marca quando NÃO está gerando e não é a ativa.
          const unread = !isGenerating && !isActive ? generation.unreadFor(conv.id) : undefined;

          return (
            <div
              key={conv.id}
              className={cn(
                "group w-full flex flex-col gap-0.5 px-3 py-1 text-xs rounded-md transition-colors",
                isActive
                  ? useGreenHighlight
                    ? "text-primary font-medium"
                    : "bg-active text-primary font-medium"
                  : "text-secondary hover:bg-hover hover:text-primary",
              )}
              style={
                isActive && useGreenHighlight ? { backgroundColor: "hsl(var(--brand-primary) / 0.10)" } : undefined
              }
            >
              <div className="flex items-center gap-1.5">
                {isGenerating ? (
                  <Loader2 className="w-3 h-3 flex-shrink-0 animate-spin text-brand-primary" />
                ) : (
                  <MessageSquare className="w-3 h-3 flex-shrink-0" />
                )}
                {isRenaming ? (
                  <input
                    ref={renameInputRef}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={handleRenameSubmit}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRenameSubmit();
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    className="flex-1 bg-transparent border-b border-color text-xs outline-none py-0"
                  />
                ) : (
                  <button
                    className="flex-1 text-left truncate"
                    onClick={() => {
                      generation.clearUnread(conv.id);
                      onBeforeSelect?.(conv);
                      setActiveConversationId(conv.id);
                      navigateToTab("chat");
                      if (isMobile) setSidebarCollapsed(true);
                    }}
                    onDoubleClick={() => {
                      setRenamingId(conv.id);
                      setRenameValue(conv.title);
                    }}
                  >
                    {conv.title}
                  </button>
                )}
                {/* Marcador de resposta nova (azul) / falha (âmbar) em background */}
                {unread === "new" && (
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-brand-primary" title="Resposta nova" />
                )}
                {unread === "failed" && (
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-amber-500" title="Geração interrompida" />
                )}
                {!isRenaming && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDeleteId(conv.id);
                      setDeleteDialogOpen(true);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-500 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deletar conversa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todas as mensagens desta conversa serão removidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Deletar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
